import {yupResolver} from '@hookform/resolvers/yup';
import PropTypes from 'prop-types';
import {useState, useEffect} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Button, Form, Grid, Label, Popup, Icon, Message, Container} from 'semantic-ui-react';
import * as yup from 'yup';

import {
  drupalSubdomain,
  validationSchemas,
  drupalMinLength,
  formatDrupalVersionsOptions,
} from 'components/Common/sharedElements';
import QrCodeModal from 'components/QrCode/QrCodeModal';
import {getChangedFieldsValues} from 'forms';
import {DrupalSiteStatus} from 'helpers/enums';
import {useReservedHostnames, useSupportedDrupalVersions} from 'hooks';
import httpClient from 'httpClient';

import RemoveDrupalEnvironmentModal from '../RemoveDrupalEnvironmentModal';

import styles from './Info.module.scss';

const formSchema = yup.object().shape({
  name: validationSchemas.drupalSiteURLValidation
    .test('domainIsUnique', 'Such domain already exists', (value, ctx) => {
      const siteDomain = value + drupalSubdomain;
      if (siteDomain === ctx.options.context.originalUrl) {
        return true;
      }

      if (ctx.options.context.reservedHostnames.includes(value)) {
        return ctx.createError({message: 'Provided site URL is a reserved name'});
      }

      return !ctx.options.context.reservedFullyQualifiedDomainNames.includes(siteDomain);
    })
    .test(
      'minLength',
      `Minimum allowed length of a site URL is ${drupalMinLength} characters`,
      (value, ctx) => {
        const originalSiteName = ctx.options.context.originalUrl.split('.')[0];
        if (originalSiteName !== undefined) {
          if (originalSiteName === value) {
            return true;
          } else {
            return value.length >= drupalMinLength;
          }
        }
      }
    ),
});

export default function Info({
  projectName,
  envName,
  url,
  version,
  onSave,
  onDelete,
  status,
  isPrimary,
  canBePrimary,
  clonedFrom,
}) {
  const {
    reservedHostnames,
    reservedFullyQualifiedDomainNames,
    isLoading: isLoadingReservedHostnames,
  } = useReservedHostnames('drupal');

  const {
    supportedDrupalVersions: drupalVersions,
    isLoading: isLoadingSupportedVersions,
    error: supportedDrupalVersionError,
  } = useSupportedDrupalVersions();

  const supportedDrupalVersions = drupalVersions.includes(version)
    ? drupalVersions
    : [version, ...drupalVersions];

  const drupalVersionsOptions = formatDrupalVersionsOptions(
    !supportedDrupalVersionError ? supportedDrupalVersions : [version]
  );

  const extraDefaultValues = {};
  const extraSchemas = {
    version: yup
      .string()
      .required('Please choose a CERN Drupal version for the new environment')
      .oneOf(supportedDrupalVersions),
  };

  if (!isPrimary) {
    extraSchemas.isPrimary = yup.bool().optional();
    extraDefaultValues.isPrimary = false;
  }

  const {
    control,
    handleSubmit,
    formState: {dirtyFields, isDirty, isValid, isSubmitting, isSubmitSuccessful},
    reset,
    getValues,
  } = useForm({
    context: {reservedHostnames, reservedFullyQualifiedDomainNames, originalUrl: url},
    mode: 'onChange',
    defaultValues: {name: url.split('.')[0], version, ...extraDefaultValues},
    resolver: yupResolver(formSchema.concat(yup.object().shape(extraSchemas))),
  });

  const canBeSetAsPrimary = typeof canBePrimary === 'boolean' && canBePrimary;

  // QR code
  const [qrUrl, setQrUrl] = useState(null);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
    }
  }, [isSubmitSuccessful, reset, getValues]);

  const submitHandler = async data => {
    const values = getChangedFieldsValues(dirtyFields, data);

    if (values.name) {
      values.url = values.name + drupalSubdomain;
      delete values.name;
    }

    if (onSave) {
      await onSave(values);
    }
  };

  return (
    <>
      <Container>
        {status === DrupalSiteStatus.INITIALIZING && (
          <Message warning>
            <Message.Header>
              <Icon name="warning" />
              This website is initializing
            </Message.Header>
            <p>
              Please wait a couple of minutes and refresh the page. If the process is taking a lot
              of time please open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=functional_element&name=drupal-infrastructure"
                target="_blank"
                rel="noreferrer"
              >
                SNOW ticket
              </a>
              .
            </p>
          </Message>
        )}
        {status === DrupalSiteStatus.BROKEN && (
          <Message warning>
            <Message.Header>
              <Icon name="warning" />
              This website has a problem
            </Message.Header>
            <p>
              You can try to recover the site by restoring it from a backup or open a{' '}
              <a
                href="https://cern.service-now.com/service-portal?id=functional_element&name=drupal-infrastructure"
                target="_blank"
                rel="noreferrer"
              >
                SNOW ticket
              </a>
              .
            </p>
          </Message>
        )}
        <Form onSubmit={handleSubmit(submitHandler)} loading={isSubmitting}>
          <Grid centered stackable>
            <Grid.Row columns={2}>
              <Grid.Column textAlign="right" width={5}>
                <div className={styles.FixedLabel}>
                  <Popup trigger={<label htmlFor="link">View site</label>} position="left center">
                    <Popup.Content>Open your site in a separate window.</Popup.Content>
                  </Popup>
                </div>
              </Grid.Column>
              <Grid.Column textAlign="left" width={11} verticalAlign="bottom">
                <a href={`https://${url}`} target="_blank" rel="noreferrer">
                  {url}
                </a>
                {isPrimary && (
                  <Popup
                    trigger={
                      <Icon
                        className={styles.PrimaryEnvironmentIndicator}
                        name="star"
                        color="yellow"
                      />
                    }
                    content="This environment is the primary one"
                    position="right center"
                  />
                )}

                {!isDirty && (
                  <Popup
                    trigger={
                      <Icon
                        className={styles.QrCodeIcon}
                        name="qrcode"
                        onClick={() => {
                          const values = getValues();
                          const currentUrl = `https://${values.name}${drupalSubdomain}`;
                          setQrUrl(currentUrl);
                        }}
                      />
                    }
                    position="top center"
                  >
                    Generate QR code
                  </Popup>
                )}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column textAlign="right" width={5}>
                <div className={styles.FixedLabel}>
                  <Popup trigger={<label htmlFor="name">URL*</label>} position="left center">
                    <Popup.Content>
                      Choose descriptive and short name, following naming conventions.
                    </Popup.Content>
                  </Popup>
                </div>
              </Grid.Column>
              <Grid.Column textAlign="left" width={11}>
                <Controller
                  control={control}
                  name="name"
                  render={({field: {onChange, value, name}, fieldState: {error}}) => (
                    <Form.Input
                      id="name"
                      width={12}
                      error={error?.message}
                      onChange={(_evt, data) => onChange(data.value)}
                      name={name}
                      value={value}
                      labelPosition="right"
                      required
                    >
                      <input />
                      <Label>{drupalSubdomain}</Label>
                    </Form.Input>
                  )}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={3}>
              <Grid.Column textAlign="right" width={5}>
                <div className={styles.FixedLabel}>
                  <Popup
                    trigger={<label htmlFor="version">CERN Drupal version*</label>}
                    position="left center"
                  >
                    <Popup.Content>
                      Choose the CERN Drupal version for the environment. The vX.Y matches the
                      Drupal Major.Minor release version.
                    </Popup.Content>
                  </Popup>
                </div>
              </Grid.Column>
              <Grid.Column textAlign="left" width={5}>
                <Controller
                  control={control}
                  name="version"
                  render={({field: {onChange, value, name}, fieldState: {error}}) => (
                    <Form.Select
                      id="version"
                      onChange={(_evt, data) => onChange(data.value)}
                      name={name}
                      value={value}
                      options={drupalVersionsOptions}
                      error={error?.message}
                      loading={isLoadingSupportedVersions}
                      fluid
                      selection
                      required
                    />
                  )}
                />
              </Grid.Column>
              <Grid.Column width={6}></Grid.Column>
            </Grid.Row>
            {!isPrimary && (
              <Grid.Row columns={3}>
                <Grid.Column textAlign="right" width={5}>
                  <Popup
                    trigger={<label htmlFor="isPrimary">Set as primary env</label>}
                    position="left center"
                  >
                    <Popup.Content>
                      Toggle to designate this environment as the primary one. Primary environment
                      is backed up automatically every 48 hours.{' '}
                      <strong>
                        Once you designate this environment as the new primary, it will inherit the
                        main URL and the aliases from the old primary environment whereas the old
                        primary environment will be assigned a random URL.
                      </strong>
                    </Popup.Content>
                  </Popup>
                </Grid.Column>
                <Grid.Column textAlign="left" width={5}>
                  <Controller
                    control={control}
                    name="isPrimary"
                    render={({field: {onChange, value, name}, fieldState: {error}}) => (
                      <Popup
                        trigger={
                          <Form.Checkbox
                            id="isPrimary"
                            onChange={(_evt, data) => onChange(data.checked)}
                            name={name}
                            checked={value}
                            error={error?.message}
                            readOnly={!canBeSetAsPrimary}
                            toggle
                          />
                        }
                        content={
                          typeof canBePrimary === 'string' ? (
                            <>
                              {canBePrimary}{' '}
                              <a
                                href="https://cern.service-now.com/service-portal?id=sc_cat_item&name=request&se=Drupal-Service"
                                target="_blank"
                                rel="noreferrer"
                              >
                                Please open a SNOW ticket
                              </a>{' '}
                              in case you want this site to become the primary one.
                            </>
                          ) : null
                        }
                        disabled={canBeSetAsPrimary}
                        position="bottom left"
                        hoverable
                      />
                    )}
                  />
                </Grid.Column>
                <Grid.Column width={6} textAlign="left">
                  {!clonedFrom && status === DrupalSiteStatus.OK && getValues().isPrimary && (
                    <Popup
                      trigger={<Icon name="warning" circular fitted />}
                      hoverable
                      position="right center"
                    >
                      If you set this site to be the primary, it won't automatically inherit the
                      Matomo Registration defined on the current primary site. Please, open a{' '}
                      <a
                        className={styles.Link}
                        href="https://cern.service-now.com/service-portal?id=sc_cat_item&name=request&fe=web-infrastructure"
                        target="_blank"
                        rel="noreferrer"
                      >
                        SNOW ticket to use an existing Matomo registration
                      </a>{' '}
                      or create a new registration from the Web Analytics tab.
                    </Popup>
                  )}
                </Grid.Column>
              </Grid.Row>
            )}
            <Grid.Row>
              <Grid.Column textAlign="right">
                <Button
                  type="submit"
                  className={styles.SaveButton}
                  disabled={
                    !isValid ||
                    !isDirty ||
                    isSubmitting ||
                    isLoadingReservedHostnames ||
                    isLoadingSupportedVersions
                  }
                >
                  Save
                </Button>
                <RemoveDrupalEnvironmentModal
                  trigger={
                    isPrimary ? (
                      <Popup
                        trigger={
                          <span>
                            <Button type="button" className={styles.DeleteButton} disabled>
                              Delete
                            </Button>
                          </span>
                        }
                        content="Deletion of a primary environment is not allowed. Please mark another environment as primary before deleting this one."
                        position="right center"
                      />
                    ) : (
                      <Button type="button" className={styles.DeleteButton}>
                        Delete
                      </Button>
                    )
                  }
                  envName={envName}
                  successMessage={`Drupal environment ${envName} has been marked for deletion and will be removed shortly.`}
                  content={
                    <div className="content">
                      You are about to delete the Drupal environment "<strong>{envName}</strong>".
                      This operation can not be undone. Are you sure that you want to proceed?
                      Please note that:
                      <ul>
                        <li>
                          It may take some time for the URL(s) to be available for reuse again.
                        </li>
                        <li>
                          In case you have registered your site for <strong>Web Analytics</strong>{' '}
                          and you don't need the analytics data anymore, you can request the
                          deletion of the Matomo registration via a{' '}
                          <a
                            className={styles.Link}
                            href="https://cern.service-now.com/service-portal?id=sc_cat_item&name=request&fe=web-infrastructure"
                            target="_blank"
                            rel="noreferrer"
                          >
                            SNOW ticket
                          </a>
                          .
                        </li>
                      </ul>
                    </div>
                  }
                  confirmButtonContent="Delete this environment"
                  onConfirm={async () => {
                    await httpClient.delete(`/api/drupal/${projectName}/drupalsites/${envName}`);
                    if (onDelete) {
                      await onDelete();
                    }
                  }}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Form>
      </Container>

      {qrUrl && (
        <QrCodeModal
          siteUrl={qrUrl}
          onClose={() => {
            setQrUrl(null);
          }}
        />
      )}
    </>
  );
}

Info.propTypes = {
  projectName: PropTypes.string.isRequired,
  envName: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  version: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  status: PropTypes.string.isRequired,
  isPrimary: PropTypes.bool.isRequired,
  canBePrimary: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  clonedFrom: PropTypes.string,
};
